import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RoutesPaths } from "../../Router/constant";
import { EyeIcon, EyeOffIcon } from "./onboarding/icons";
import {
  Zap,
  CalendarDays,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  User,
  Mail,
  Lock,
  MessageSquare,
  BarChart3,
  Star,
} from "lucide-react";
import { googleLoginAction, signupAction } from "../../actions/auth";
import { resolveAuthLanding } from "../../utils/authLanding";



// const RobotMascot = () => (
//   <svg viewBox="0 0 220 230" className="auth-mascot-svg" aria-hidden="true">
//     <ellipse cx="110" cy="212" rx="55" ry="10" fill="#000" opacity="0.06" />

//     <line
//       x1="110"
//       y1="14"
//       x2="110"
//       y2="34"
//       stroke="#c9beff"
//       strokeWidth="4"
//       strokeLinecap="round"
//     />
//     <circle cx="110" cy="10" r="7" fill="var(--hostor-primary)" />

//     <path
//       d="M158 108c14 4 22 16 22 30s-10 26-10 26"
//       fill="none"
//       stroke="#e6e1fb"
//       strokeWidth="14"
//       strokeLinecap="round"
//     />
//     <circle
//       cx="170"
//       cy="160"
//       r="11"
//       fill="#fff"
//       stroke="#d8d2f7"
//       strokeWidth="3"
//     />

//     <rect
//       x="60"
//       y="120"
//       width="100"
//       height="80"
//       rx="26"
//       fill="#fff"
//       stroke="#d8d2f7"
//       strokeWidth="3"
//     />
//     <circle cx="88" cy="160" r="5" fill="var(--hostor-primary)" />
//     <circle cx="132" cy="160" r="5" fill="var(--hostor-primary)" />
//     <path
//       d="M92 176c6 7 12 10 18 10s12-3 18-10"
//       stroke="var(--hostor-primary)"
//       strokeWidth="4"
//       fill="none"
//       strokeLinecap="round"
//     />

//     <rect
//       x="42"
//       y="30"
//       width="136"
//       height="96"
//       rx="34"
//       fill="#fff"
//       stroke="#d8d2f7"
//       strokeWidth="3"
//     />
//     <rect
//       x="62"
//       y="52"
//       width="96"
//       height="54"
//       rx="20"
//       fill="var(--hostor-primary)"
//     />
//     <circle cx="92" cy="79" r="6" fill="#fff" />
//     <circle cx="128" cy="79" r="6" fill="#fff" />
//     <path
//       d="M96 92c5 5 10 7 14 7s9-2 14-7"
//       stroke="#fff"
//       strokeWidth="3.5"
//       fill="none"
//       strokeLinecap="round"
//     />
//   </svg>
// );

// The three reasons on the left, each with the line that answers "what
// does that mean for me".
const FEATURES = [
  {
    icon: Zap,
    tone: "purple",
    title: "Set up in less than 5 minutes",
    detail: "Quick and easy setup",
  },
  {
    icon: CalendarDays,
    tone: "blue",
    title: "7-day free trial",
    detail: "Explore all premium features",
  },
  {
    icon: ShieldCheck,
    tone: "green",
    title: "Cancel anytime",
    detail: "No credit card required",
  },
];

export default function SignUp({
  onSuccess,
  onGoogleSuccess,
  onSwitchToSignIn,
}) {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const dispatch = useDispatch();

  const handleGoogleLogin = async () => {
    setError("");
    setGoogleLoading(true);

    const result = await dispatch(googleLoginAction());

    if (result?.success) {
      // Google gives a verified address and a live session, so this skips the
      // OTP step that onSuccess() drives — passing it undefined details used to
      // bounce the user off the OTP page and onto the sign-in form.
      if (onGoogleSuccess) {
        onGoogleSuccess();
        return;
      }

      // Rendered standalone at /signup there is no parent to hand off to.
      const landing = await resolveAuthLanding(dispatch);
      navigate(landing.path, { replace: true, state: landing.state });
      return;
    }

    setGoogleLoading(false);

    // A closed popup is a deliberate cancel, not something to warn about.
    if (!result?.cancelled && result?.message) {
      setError(result.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fullName.trim() || !email.trim() || !password.trim()) {
      setError("Please fill in your name, email and password.");
      return;
    }
    if (!email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setError("");
    setSubmitting(true);

    const result = await dispatch(
      signupAction({
        email: email.trim(),
        password,
        name: fullName.trim(),
      })
    );

    setSubmitting(false);

    if (!result?.success) {
      setError(result?.message || "Something went wrong. Please try again.");
      return;
    }

    const details = { fullName: fullName.trim(), email: email.trim() };

    // Rendered standalone at /signup there is no onSuccess prop, so head to
    // the OTP step directly.
    if (onSuccess) {
      onSuccess(details);
    } else {
      navigate(RoutesPaths.VERIFY_OTP, { state: details });
    }
  };

  return (
    <div className="auth-split">
      <div className="auth-split__brand auth-split__brand--light">
        <span className="auth-split__dots" aria-hidden="true" />

        <div className="auth-split__brand-inner">
          <div className="auth-brand-copy">
          {/* <span className="auth-split__logo auth-split__logo--dark">
            hostor
          </span> */}
          <div className="auth-split__logoImg">
            <img src="/assets/logo.svg" alt="" />
          </div>

          <div className="auth-split__welcome auth-split__welcome--top">
            {/* Broken here on purpose: the product name starts the second
                line, which is what makes the two lines read as one phrase
                rather than wherever the measure happens to break them. */}
            <h1>
              Create your
              <br />
              <span>Hostor</span> account
            </h1>
            <p>
              Start your <strong>7-day free trial</strong>. No credit card
              required. Set up your AI receptionist in minutes and start
              engaging your website visitors.
            </p>
          </div>

          <ul className="auth-feature-list">
            {FEATURES.map(({ icon: Icon, tone, title, detail }) => (
              <li key={title}>
                <span className={`auth-feature-icon auth-feature-icon--${tone}`}>
                  <Icon size={18} strokeWidth={2.2} />
                </span>
                <span className="auth-feature-text">
                  <strong>{title}</strong>
                  <span>{detail}</span>
                </span>
              </li>
            ))}
          </ul>

          {/* Who else is already doing this, under the reasons to. */}
          <div className="auth-proof">
            <span className="auth-proof__faces" aria-hidden="true">
              <span>RK</span>
              <span>AM</span>
              <span>JD</span>
            </span>
            <span className="auth-proof__text">
              <span className="auth-proof__stars" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={13} strokeWidth={0} fill="currentColor" />
                ))}
              </span>
              <strong>Trusted by 10,000+ businesses</strong>
              <span>to automate customer support</span>
            </span>
          </div>

          </div>

          <div className="auth-mascot">
            {/* The bubble and the tiles are placed against the robot's own
                box, not the column's: the column is the full height of the
                panel, so a percentage from its top put the bubble somewhere
                above the robot's head entirely. */}
            <div className="auth-mascot__stage">
              <img
                src="/assets/Robot-Mascot.png"
                alt=""
                className="img-fluid robotImage"
              />

              <div className="auth-mascot__bubble">
                <Sparkles size={16} strokeWidth={2.4} />
                <span>
                  Let&apos;s build your
                  <strong>AI receptionist!</strong>
                </span>
              </div>

            {/* The three things it does, floating around it. Decoration, so
                they are hidden from a screen reader rather than read out as
                three stray icons. */}
            <span className="auth-mascot__tiles" aria-hidden="true">
              <span className="auth-mascot__tile auth-mascot__tile--chat">
                <MessageSquare size={18} strokeWidth={2.2} />
              </span>
              <span className="auth-mascot__tile auth-mascot__tile--cal">
                <CalendarDays size={18} strokeWidth={2.2} />
              </span>
              <span className="auth-mascot__tile auth-mascot__tile--chart">
                <BarChart3 size={18} strokeWidth={2.2} />
              </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-split__form">
        <div className="auth-split__form-inner">
          <div className="auth-split__form-header">
            <h2>Sign up</h2>
            <p>
              Already have an account?{" "}
              <button
                type="button"
                className="auth-inline-link"
                onClick={
                  onSwitchToSignIn || (() => navigate(RoutesPaths.LOGIN))
                }
              >
                Sign in
              </button>
            </p>
          </div>

          <div className="auth-social-stack">
            <button
              type="button"
              className="auth-social-btn auth-social-btn--wide"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
            >
              <img src="/assets/google-icon.svg" alt="" />
              {googleLoading ? "Signing in…" : "Sign up with Google"}
            </button>
            {/* <button
              type="button"
              className="auth-social-btn auth-social-btn--wide"
            >
              <img src="/assets/microsoft-icon.svg" alt="" />
              Sign up with Microsoft
            </button> */}
          </div>

          <div className="auth-divider">
            <span>or continue with email</span>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {error && <p className="auth-error">{error}</p>}

            <label className="auth-field auth-field--iconed">
              <span>Full name</span>
              <span className="auth-field__icon" aria-hidden="true">
                <User size={18} strokeWidth={1.9} />
              </span>
              <input
                type="text"
                className="auth-plain-input"
                placeholder="Enter your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
              />
            </label>

            <label className="auth-field auth-field--iconed">
              <span>Work email</span>
              <span className="auth-field__icon" aria-hidden="true">
                <Mail size={18} strokeWidth={1.9} />
              </span>
              <input
                type="email"
                className="auth-plain-input"
                placeholder="Enter your work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>

            <label className="auth-field auth-field--iconed">
              <span>Password</span>
              <span className="auth-field__icon" aria-hidden="true">
                <Lock size={18} strokeWidth={1.9} />
              </span>
              <span className="auth-plain-input-wrap">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </span>
            </label>

            <button
              type="submit"
              className="onboarding__btn onboarding__btn--primary auth-submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                "Creating account..."
              ) : (
                <>
                  Create account
                  <ArrowRight size={18} strokeWidth={2.4} />
                </>
              )}
            </button>
          </form>

          <p className="auth-legal-text">
            By creating an account, you agree to our{" "}
            <a href="https://www.hostor.ai/terms/" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a> and{" "}
            <a href="https://www.hostor.ai/privacy/" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
}
